import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, User, Phone, Apple } from "lucide-react";
import { useAuth } from "../lib/authContext";
import { formatApiError } from "../lib/authApi";
import { Input } from "../components/ui/input";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton from "../components/GoogleSignInButton";

// BR phone mask: (11) 99876-5432 or (11) 9876-5432
function formatBRPhone(v) {
  const digits = (v || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export default function Cadastro() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("A senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: phone.trim() || null,
      });
      toast.success("Conta criada!");
      // Sempre que possível, novos cadastros caem no onboarding antes do next.
      navigate(`/bem-vindo?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto section-padding py-10" data-testid="cadastro-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="cadastro-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Sua caminhada</p>
      <h1 className="font-display text-4xl text-navy mt-2">Criar conta</h1>
      <p className="text-navy/65 mt-3 text-sm">
        30 segundos. Sem cartão. Salve sua jornada na nuvem.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="cadastro-form">
        <GoogleSignInButton next={next} label="Criar conta com Google" testid="cadastro-google" />

        <button
          type="button"
          disabled
          aria-disabled="true"
          data-testid="cadastro-apple-soon"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 bg-white text-navy/40 px-6 py-3 text-[11px] uppercase tracking-widest cursor-not-allowed"
          title="Em breve"
        >
          <Apple className="w-4 h-4" />
          Continuar com Apple
          <span className="ml-1 px-2 py-0.5 rounded-full text-[9px] bg-navy/5 border border-navy/10 tracking-[0.2em]">
            em breve
          </span>
        </button>

        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-navy/40">
          <span className="flex-1 h-px bg-border" />
          ou com e-mail
          <span className="flex-1 h-px bg-border" />
        </div>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Primeiro nome</span>
          <div className="relative mt-2">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <Input
              type="text"
              autoComplete="given-name"
              required
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Maria"
              className="pl-10 h-11 bg-white"
              data-testid="cadastro-name"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.3em] text-navy/55">E-mail</span>
          <div className="relative mt-2">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="pl-10 h-11 bg-white"
              data-testid="cadastro-email"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.3em] text-navy/55">
            WhatsApp{" "}
            <span className="text-navy/35 normal-case tracking-normal text-[11px]">
              (opcional)
            </span>
          </span>
          <div className="relative mt-2">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <Input
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              maxLength={16}
              value={phone}
              onChange={(e) => setPhone(formatBRPhone(e.target.value))}
              placeholder="(11) 99876-5432"
              className="pl-10 h-11 bg-white"
              data-testid="cadastro-phone"
            />
          </div>
          <span className="text-[11px] text-navy/45 mt-1.5 block">
            Usado apenas para envio de oração e contato pastoral, quando você pedir.
          </span>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Senha (mín. 8)</span>
          <div className="mt-2">
            <PasswordInput
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="mínimo 8 caracteres"
              testId="cadastro-password"
              toggleTestId="cadastro-password-toggle"
            />
          </div>
        </label>

        {error && (
          <p className="text-sm text-red-600" data-testid="cadastro-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="cadastro-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
        >
          {submitting ? "Criando..." : "Criar conta gratuita"}
        </button>

        <p className="text-[12px] text-navy/60">
          Já tem conta?{" "}
          <Link
            to={`/entrar${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="text-gold hover:text-navy"
            data-testid="cadastro-login-link"
          >
            Entrar
          </Link>
        </p>
      </form>
    </div>
  );
}
